import React, { useMemo, useState } from "react";
import { Modal, Label, ModalHeader, Card, CardBody, Row, Col, Alert, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import defaultUserImg from "../../../assets/images/defaultuserimgdark.png";
import axios from "axios";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "Components/Common/Breadcrumb";
import TableContainer from "Components/Common/TableContainer";
import { CFForm } from "./CFForm";
import { OrderStatus } from "./OrderStatus";
import classnames from 'classnames';


interface column {
    header: string;
    accessorKey?: string;
    accessorFn?: (row: any) => string;
    enableColumnFilter: boolean;
    enableSorting: boolean;
    cell?: (info: any) => React.ReactNode;
}

export const CFPlans = () => {

    const [plans, setPlans] = useState<any[]>([]);
    const [stateCode, setStateCode] = useState<string>("VA");
    const [entityType, setEntityType] = useState<string>("LLC");
    const [existingOrder, setExistingOrder] = useState<Record<string, any> | null>(null);
    const [orders, setOrders] = useState<any[]>([]);
    const [showOrders, setShowOrders] = useState<boolean>(false);
    const [showPlansView, setShowPlansView] = useState<boolean>(false);
    const [uploadModal, setUploadModal] = useState<{open: boolean, orderGuid: string | null}>({open: false, orderGuid: null});
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [activeTab, setActiveTab] = useState<string>('1');

    // Company formation contact information (can be overridden via environment variables)
    const companyFormationContactEmail = process.env.REACT_APP_COMPANY_FORMATION_CONTACT_EMAIL || 'customerservice@corpnet.com';
    const companyFormationContactPhoneRaw = process.env.REACT_APP_COMPANY_FORMATION_CONTACT_PHONE || '18668135237';
    // Format phone number for display (1-866-813-5237)
    const companyFormationContactPhone = companyFormationContactPhoneRaw.length === 11 
        ? `${companyFormationContactPhoneRaw[0]}-${companyFormationContactPhoneRaw.slice(1, 4)}-${companyFormationContactPhoneRaw.slice(4, 7)}-${companyFormationContactPhoneRaw.slice(7)}`
        : companyFormationContactPhoneRaw;

    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });

    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const fetch_plans = async (customEntityType?: string, customStateCode?: string) => {
        console.log("Fetching plans...");

        try {
            setshowloader(true);
            const response = await axios.get(
                `/companyFormation/listPlans1`,
                {
                    params: {
                        "entityType": customEntityType || entityType,
                        "state": customStateCode || stateCode,
                        "filing": "standard"
                    }
                },
                { withCredentials: true }
            );

            if (response && response.status === "SENT" && response.plans.statusCode === "200") {
                setPlans(response.plans.value.packageCollection || []);
            } else {
                setPlans([]);
            }

            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const fetch_orders = async () => {
        console.log("Fetching orders...");

        try {
            const response = await axios.get(
                `/companyFormation/listOrders1/`,
                { withCredentials: true }
            );

            if (response && response.status === "success") {
                setOrders(response.orders || []);
            } else {
                setOrders([]);
            }
        } catch (error: any) {
            console.error("Error fetching orders:", error);
            setOrders([]);
        }
    };

    const cancelOrder = async (orderGuid?: string) => {
        console.log("Canceling order...");

        try {
            setshowloader(true);
            const response = await axios.post(
                `/companyFormation/cancelCorpnetOrder`,
                {
                    "orderGuid": orderGuid || existingOrder?.orderGuid,
                },
                { withCredentials: true }
            );

            if (response && response.status === "CANCELLED") {
                // Refresh both plans and orders
                fetch_plans();
                fetch_orders();
                setAlertMsgg({ ...alertMsgg, open: true, content: "Order cancelled successfully", type: "success" });
            } else {
                console.log("Failed to cancel order", response);
                setAlertMsgg({ ...alertMsgg, open: true, content: "Failed to cancel order", type: "error" });
            }

            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const uploadDocument = async () => {
        if (!selectedFile || !uploadModal.orderGuid) {
            setAlertMsgg({ ...alertMsgg, open: true, content: "Please select a file to upload", type: "error" });
            return;
        }

        try {
            setshowloader(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('orderId', uploadModal.orderGuid);

            const response = await axios.post(
                `/companyFormation/uploadOrderDocument`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true
                }
            );

            if (response && response.status === "success") {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Document uploaded successfully", type: "success" });
                setUploadModal({open: false, orderGuid: null});
                setSelectedFile(null);
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Failed to upload document", type: "error" });
            }

            setshowloader(false);
        } catch (error: any) {
            console.error("Error uploading document:", error);
            setAlertMsgg({ ...alertMsgg, open: true, content: "Failed to upload document", type: "error" });
            setshowloader(false);
        }
    };

    useEffect(() => {
        fetch_plans();
        fetch_orders();
    }, []);

    useEffect(() => {
        // If there are orders, show orders view by default, otherwise show plans
        if (orders.length > 0) {
            setShowPlansView(false);
        } else {
            setShowPlansView(true);
        }
    }, [orders]);

    document.title = "Company Formation | BizServe";

    const [hoveredRow, setHoveredRow] = useState<number | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<{ [pkgIdx: number]: { [productId: string]: string } }>({});
    const [formOpenPkgIdx, setFormOpenPkgIdx] = useState<number | null>(null);

    // Set initial selectedOptions from API on plans load
    useEffect(() => {
        const initialSelected: { [pkgIdx: number]: { [productId: string]: string } } = {};
        // plans is an array of packages, not plans, so use allPackages logic
        let pkgIdx = 0;
        plans.forEach(plan => {
            (plan.productPackages || []).forEach(pkg => {
                (pkg.productOptions || []).forEach((opt: any) => {
                    if (opt.packageDisplaySelection === "Selected") {
                        if (!initialSelected[pkgIdx]) initialSelected[pkgIdx] = {};
                        initialSelected[pkgIdx][opt.productId] = "Selected";
                    }
                });
                pkgIdx++;
            });
        });
        setSelectedOptions(initialSelected);
    }, [plans]);

    // Helper to handle checkbox selection for Optional/Selected
    const handleCheckboxChange = (pkgIdx: number, productId: string, checked: boolean) => {
        setSelectedOptions(prev => {
            // Find all packages that have this productId as Optional/Selected
            const newSelected = { ...prev };
            Object.keys(newSelected).forEach(idx => {
                if (newSelected[idx][productId]) {
                    newSelected[idx][productId] = "";
                }
            });
            if (checked) {
                if (!newSelected[pkgIdx]) newSelected[pkgIdx] = {};
                newSelected[pkgIdx][productId] = "Selected";
            }
            return { ...newSelected };
        });
    };

    // Helper to group product options by productConstraint for single-choice groups
    const getProductConstraintGroups = (allPackages: any[]) => {
        const constraintGroups: { [constraint: string]: string[] } = {};
        allPackages.forEach(pkg => {
            (pkg.productOptions || []).forEach((opt: any) => {
                if (opt.productConstraint) {
                    if (!constraintGroups[opt.productConstraint]) {
                        constraintGroups[opt.productConstraint] = [];
                    }
                    if (!constraintGroups[opt.productConstraint].includes(opt.productId)) {
                        constraintGroups[opt.productConstraint].push(opt.productId);
                    }
                }
            });
        });
        return constraintGroups;
    };

    // Dropdown options
    const stateOptions = [
        { code: "AL", name: "Alabama" },
        { code: "AK", name: "Alaska" },
        { code: "AZ", name: "Arizona" },
        { code: "AR", name: "Arkansas" },
        { code: "CA", name: "California" },
        { code: "CO", name: "Colorado" },
        { code: "CT", name: "Connecticut" },
        { code: "DE", name: "Delaware" },
        { code: "FL", name: "Florida" },
        { code: "GA", name: "Georgia" },
        { code: "HI", name: "Hawaii" },
        { code: "ID", name: "Idaho" },
        { code: "IL", name: "Illinois" },
        { code: "IN", name: "Indiana" },
        { code: "IA", name: "Iowa" },
        { code: "KS", name: "Kansas" },
        { code: "KY", name: "Kentucky" },
        { code: "LA", name: "Louisiana" },
        { code: "ME", name: "Maine" },
        { code: "MD", name: "Maryland" },
        { code: "MA", name: "Massachusetts" },
        { code: "MI", name: "Michigan" },
        { code: "MN", name: "Minnesota" },
        { code: "MS", name: "Mississippi" },
        { code: "MO", name: "Missouri" },
        { code: "MT", name: "Montana" },
        { code: "NE", name: "Nebraska" },
        { code: "NV", name: "Nevada" },
        { code: "NH", name: "New Hampshire" },
        { code: "NJ", name: "New Jersey" },
        { code: "NM", name: "New Mexico" },
        { code: "NY", name: "New York" },
        { code: "NC", name: "North Carolina" },
        { code: "ND", name: "North Dakota" },
        { code: "OH", name: "Ohio" },
        { code: "OK", name: "Oklahoma" },
        { code: "OR", name: "Oregon" },
        { code: "PA", name: "Pennsylvania" },
        { code: "RI", name: "Rhode Island" },
        { code: "SC", name: "South Carolina" },
        { code: "SD", name: "South Dakota" },
        { code: "TN", name: "Tennessee" },
        { code: "TX", name: "Texas" },
        { code: "UT", name: "Utah" },
        { code: "VT", name: "Vermont" },
        { code: "VA", name: "Virginia" },
        { code: "WA", name: "Washington" },
        { code: "WV", name: "West Virginia" },
        { code: "WI", name: "Wisconsin" },
        { code: "WY", name: "Wyoming" }
    ];
    const entityOptions = [
        { value: "LLC", label: "LLC" },
        { value: "C-Corporation", label: "C-Corporation" },
        { value: "S-Corporation", label: "S-Corporation" },
        { value: "Professional Corporation", label: "Professional Corporation" },
        { value: "Non-Profit Corporation", label: "Non-Profit Corporation" },
    ];

    // Helper to get selected productIds for a package
    const getSelectedProductIds = (pkgIdx: number) => {
        const opts = selectedOptions[pkgIdx] || {};
        return Object.entries(opts)
            .filter(([_, val]) => val === "Selected")
            .map(([productId]) => productId);
    };

    // Helper to get total price for a package (base + selected products)
    const getTotalPriceForPackage = (pkgIdx: number, allPackages: any[]) => {
        const pkg = allPackages[pkgIdx];
        if (!pkg) return 0;
        let total = Number(pkg.price) || 0;
        const opts = selectedOptions[pkgIdx] || {};
        (pkg.productOptions || []).forEach((opt: any) => {
            if (
                (opt.packageDisplaySelection === "Selected" || opt.packageDisplaySelection === "Optional") &&
                opts[opt.productId] === "Selected" &&
                opt.price != null
            ) {
                total += Number(opt.price);
            }
        });
        return total;
    };

    const handleSelectAndScroll = (idx, allPackages) => {
        const selectedProducts = getSelectedProductIds(idx);
        const totalPrice = getTotalPriceForPackage(idx, allPackages).toFixed(2);
        console.log('Selected products for package', idx, selectedProducts, 'Total price:', totalPrice);
        setFormOpenPkgIdx(idx);
        setTimeout(() => {
            const anchor = document.getElementById('cf-form-anchor');
            if (anchor) {
                anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    return (
        <div className="">
            <div className="container-fluid">
                {existingOrder ? (<Breadcrumb title="Company Formation" breadcrumbItem="Order" />) :
                    (<Breadcrumb title="Company Formation" breadcrumbItem="Plans" />)}
                {alertMsgg.open && (
                    <Alert
                        color={alertMsgg.type === "success" ? "success" : "danger"}
                        toggle={handleAlertMsggClose}
                        fade={false}
                        style={{ fontWeight: "bold" }}
                    >
                        {alertMsgg.content}
                    </Alert>
                )}
                <div className="loaderContainer" hidden={!showloader}>
                    <div className="spinner">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
                {/* Orders Section */}
                {orders.length > 0 && !showPlansView && (
                    <Card className="mb-4">
                        <CardBody>
                            <div 
                                className="d-flex justify-content-between align-items-center cursor-pointer"
                                onClick={() => setShowOrders(!showOrders)}
                                style={{ cursor: 'pointer' }}
                            >
                                <h5 className="mb-0">
                                    <i className={`bx ${showOrders ? 'bx-chevron-down' : 'bx-chevron-right'} me-2`}></i>
                                    Your Orders ({orders.length})
                                </h5>
                            </div>
                            
                            {showOrders && (
                                <div className="mt-3">
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Order ID</th>
                                                    <th>Entity Type</th>
                                                    <th>State</th>
                                                    <th>Price</th>
                                                    <th>Status</th>
                                                    <th>Created</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orders.map((order, index) => {
                                                    const orderDetails = order.orderDetails?.data?.partnerOrder || {};
                                                    const totalPrice = orderDetails.orderTotalPrice || 0;
                                                    const businessType = orderDetails.businessStructureType || 'N/A';
                                                    const state = orderDetails.businessStateInitial || 'N/A';
                                                    // Get package and product names from snapshot (if available)
                                                    const selectedItems = order.selected_items_snapshot || null;
                                                    const packageName = selectedItems?.package?.name || 'N/A';
                                                    const productNames = selectedItems?.products?.map((p: any) => p.name).filter(Boolean) || [];
                                                    
                                                    return (
                                                        <tr key={order.id || index}>
                                                            <td>
                                                                <code>{order.guid || 'N/A'}</code>
                                                            </td>
                                                            <td>{businessType}</td>
                                                            <td>{state}</td>
                                                            <td>${totalPrice.toLocaleString()}</td>
                                                            <td>
                                                                <span className={`badge ${
                                                                    order.status === 'CANCELLED' ? 'bg-danger' :
                                                                    order.status === 'COMPLETED' ? 'bg-success' :
                                                                    order.status === 'PENDING' ? 'bg-warning' :
                                                                    'bg-info'
                                                                }`}>
                                                                    {order.status || 'Unknown'}
                                                                </span>
                                                            </td>
                                                            <td>{new Date(order.created_on).toLocaleDateString()}</td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    {order.status !== 'CANCELLED' && (
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => setUploadModal({open: true, orderGuid: order.guid})}
                                                                        >
                                                                            <i className="bx bx-upload me-1"></i>
                                                                            Upload Docs
                                                                        </button>
                                                                    )}
                                                                    {order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => {
                                                                                if (window.confirm('Are you sure you want to cancel this order?')) {
                                                                                    cancelOrder(order.guid);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <i className="bx bx-x me-1"></i>
                                                                            Cancel
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                {/* Toggle Button - Show when there are orders */}
                {orders.length > 0 && (
                    <div className="text-center mb-4">
                        <button 
                            className={`btn ${showPlansView ? 'btn-outline-secondary' : 'btn-primary'}`}
                            onClick={() => setShowPlansView(!showPlansView)}
                        >
                            <i className={`bx ${showPlansView ? 'bx-list-ul' : 'bx-plus-circle'} me-2`}></i>
                            {showPlansView ? 'View Orders' : 'View Plans / Create New Order'}
                        </button>
                    </div>
                )}

                {/* Plans Section */}
                {showPlansView && (
                    <>
                        {/* Tabs Navigation */}
                        <Nav tabs className="nav-tabs-custom mb-4">
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={classnames({ active: activeTab === '1' })}
                                    onClick={() => { setActiveTab('1'); }}
                                >
                                    <span className="d-block d-sm-none"><i className="fas fa-list"></i></span>
                                    <span className="d-none d-sm-block">Plans</span>
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={classnames({ active: activeTab === '2' })}
                                    onClick={() => { setActiveTab('2'); }}
                                >
                                    <span className="d-block d-sm-none"><i className="fas fa-envelope"></i></span>
                                    <span className="d-none d-sm-block">Contact</span>
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            {/* Plans Tab */}
                            <TabPane tabId="1">
                                {/* State and Entity Type Dropdowns */}
                                {existingOrder ? (
                            <OrderStatus 
                                orderPhase={existingOrder.orderDetails.orderPhase}
                                onCancel={() => {
                                    cancelOrder();
                                }}
                                orderId={existingOrder.orderGuid}
                            />
                        ) : (<><Row className="mb-4">
                    <Col md={3}>
                        <label className="form-label">State</label>
                        <select
                            className="form-control"
                            value={stateCode}
                            onChange={e => {
                                setStateCode(e.target.value);
                                fetch_plans(entityType, e.target.value);
                            }}
                        >
                            {stateOptions.map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.name}</option>
                            ))}
                        </select>
                    </Col>
                    <Col md={3}>
                        <label className="form-label">Entity Type</label>
                        <select
                            className="form-control"
                            value={entityType}
                            onChange={e => {
                                setEntityType(e.target.value);
                                fetch_plans(e.target.value, stateCode);
                            }}
                        >
                            {entityOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </Col>
                </Row>
                    {/* Plans Table */}
                    <Row>
                        <Col md={12}>
                            <Card className="border shadow-sm">
                                <CardBody>
                                    <h4 className="mb-4">Compare Plans</h4>
                                    {/* Build table data */}
                                    {(() => {
                                        // Collect all unique product names and their displaySequence
                                        const productMap = new Map<string, number>();
                                        plans.forEach(plan => {
                                            (plan.productPackages || []).forEach(pkg => {
                                                (pkg.productOptions || []).forEach((opt: any) => {
                                                    if (!productMap.has(opt.productName)) {
                                                        productMap.set(opt.productName, opt.displaySequence ?? 0);
                                                    }
                                                });
                                            });
                                        });
                                        // Sort products by displaySequence
                                        const allProducts = Array.from(productMap.entries())
                                            .sort((a, b) => (a[1] ?? 0) - (b[1] ?? 0))
                                            .map(([name]) => name);

                                        // Collect all packages (columns) and sort by collectionDisplaySequence
                                        const allPackages: any[] = [];
                                        plans.forEach(plan => {
                                            (plan.productPackages || []).forEach(pkg => {
                                                allPackages.push(pkg);
                                            });
                                        });
                                        allPackages.sort((a, b) => (a.collectionDisplaySequence ?? 0) - (b.collectionDisplaySequence ?? 0));

                                        // Helper to check if a package includes a product
                                        const hasProduct = (pkg: any, productName: string) =>
                                            (pkg.productOptions || []).some((opt: any) => opt.productName === productName);

                                        // Build productConstraint groups for single-choice options
                                        const constraintGroups = getProductConstraintGroups(allPackages);

                                        // Helper to find the constraint group for a productId
                                        const findConstraintGroup = (productId: string) => {
                                            for (const [constraint, ids] of Object.entries(constraintGroups)) {
                                                if (ids.includes(productId)) return { constraint, ids };
                                            }
                                            return null;
                                        };

                                        // Helper to handle radio selection for constraint group
                                        const handleConstraintRadioChange = (pkgIdx: number, constraint: string, productId: string) => {
                                            setSelectedOptions(prev => {
                                                const newSelected = { ...prev };
                                                if (!newSelected[pkgIdx]) newSelected[pkgIdx] = {};
                                                // Unselect all in this constraint group for this package
                                                constraintGroups[constraint].forEach(pid => {
                                                    newSelected[pkgIdx][pid] = "";
                                                });
                                                // Select the chosen one
                                                newSelected[pkgIdx][productId] = "Selected";
                                                return { ...newSelected };
                                            });
                                        };

                                        return (
                                            <div className="table-responsive">
                                                <table className="table table-bordered align-middle text-center">
                                                    <thead>
                                                        <tr>
                                                            <th className="text-start">Product</th>
                                                            {allPackages.map((pkg, idx) => (
                                                                <th key={pkg.id || idx}>
                                                                    <div className="fw-bold">{pkg.name}</div>
                                                                    <div className="small text-muted">
                                                                        {pkg.price < 0 ? `$${Math.abs(pkg.price).toFixed(2)}` : `$${pkg.price?.toFixed(2)}`}
                                                                    </div>
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {allProducts.map((productName, rowIdx) => {
                                                            // Find the first product option with a description for tooltip
                                                            let productDesc = "";
                                                            let productId = "";
                                                            for (const pkg of allPackages) {
                                                                const opt = (pkg.productOptions || []).find((o: any) => o.productName === productName && o.description);
                                                                if (opt && opt.description) {
                                                                    productDesc = opt.description;
                                                                    productId = opt.productId;
                                                                    break;
                                                                }
                                                            }

                                                            return (
                                                                <tr key={productName}>
                                                                    <td
                                                                        className="text-start"
                                                                        style={{ position: "relative", cursor: productDesc ? "pointer" : "default" }}
                                                                        onMouseEnter={() => productDesc && setHoveredRow(rowIdx)}
                                                                        onMouseLeave={() => productDesc && setHoveredRow(null)}
                                                                    >
                                                                        <span>{productName}</span>
                                                                        {productDesc && (
                                                                            <>
                                                                                <i
                                                                                    className="bx bx-info-circle text-info ms-2"
                                                                                    style={{ fontSize: 18, verticalAlign: "middle" }}
                                                                                    title="More info"
                                                                                ></i>
                                                                                <div
                                                                                    style={{
                                                                                        minWidth: 400,
                                                                                        maxWidth: 800,
                                                                                        background: "#f8f9fa",
                                                                                        border: "1px solid #ddd",
                                                                                        borderRadius: 6,
                                                                                        padding: "8px 12px",
                                                                                        position: "absolute",
                                                                                        left: "110%",
                                                                                        top: 0,
                                                                                        zIndex: 10,
                                                                                        whiteSpace: "pre-line",
                                                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                                                        display: hoveredRow === rowIdx ? "block" : "none"
                                                                                    }}
                                                                                >
                                                                                    {productDesc}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </td>
                                                                    {allPackages.map((pkg, colIdx) => {
                                                                        const opt = (pkg.productOptions || []).find((o: any) => o.productName === productName);
                                                                        if (!opt) {
                                                                            return (
                                                                                <td key={colIdx}>
                                                                                    <span className="text-muted">
                                                                                        <i className="bx bx-x-circle font-size-20"></i>
                                                                                    </span>
                                                                                </td>
                                                                            );
                                                                        }

                                                                        // Bundled
                                                                        if (opt.packageDisplaySelection === "Bundled") {
                                                                            return (
                                                                                <td key={colIdx}>
                                                                                    <span className="text-success">
                                                                                        <i className="bx bx-check font-size-20"></i>
                                                                                    </span>
                                                                                    {opt.packageDisplaySelectionSubtitle && (
                                                                                        <div className="small text-info mt-1">{opt.packageDisplaySelectionSubtitle}</div>
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        }
                                                                        // Required
                                                                        if (opt.packageDisplaySelection === "Required") {
                                                                            return (
                                                                                <td key={colIdx}>
                                                                                    <span className="text-success">
                                                                                        <i className="bx bx-check-circle font-size-20"></i>
                                                                                    </span>
                                                                                    {opt.price != null && (
                                                                                        <div className="small text-muted">${opt.price}</div>
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        }
                                                                        // Selected or Optional: show as checkbox or radio if grouped
                                                                        if (opt.packageDisplaySelection === "Selected" || opt.packageDisplaySelection === "Optional") {
                                                                            // If this product is part of a constraint group with more than one option, show as radio
                                                                            const constraintGroup = opt.productConstraint && findConstraintGroup(opt.productId);
                                                                            if (constraintGroup && constraintGroup.ids.length > 1) {
                                                                                // Determine checked state: use selectedOptions if user has interacted, else use API value
                                                                                const isChecked =
                                                                                    (selectedOptions[colIdx] && selectedOptions[colIdx][opt.productId] === "Selected") ||
                                                                                    (!selectedOptions[colIdx] && opt.packageDisplaySelection === "Selected");
                                                                                return (
                                                                                    <td key={colIdx}>
                                                                                        <input
                                                                                            type="radio"
                                                                                            name={`constraint-${constraintGroup.constraint}-${colIdx}`}
                                                                                            checked={isChecked}
                                                                                            onChange={() => handleConstraintRadioChange(colIdx, constraintGroup.constraint, opt.productId)}
                                                                                        />
                                                                                        {opt.price != null && (
                                                                                            <span className="small text-muted ms-1">${opt.price}</span>
                                                                                        )}
                                                                                    </td>
                                                                                );
                                                                            }
                                                                            // Otherwise, normal checkbox
                                                                            const isChecked =
                                                                                (selectedOptions[colIdx] && selectedOptions[colIdx][opt.productId] === "Selected") ||
                                                                                (!selectedOptions[colIdx] && opt.packageDisplaySelection === "Selected");
                                                                            return (
                                                                                <td key={colIdx}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={isChecked}
                                                                                        onChange={e => handleCheckboxChange(colIdx, opt.productId, e.target.checked)}
                                                                                    />
                                                                                    {opt.price != null && (
                                                                                        <span className="small text-muted ms-1">${opt.price}</span>
                                                                                    )}
                                                                                </td>
                                                                            );
                                                                        }
                                                                        // Default
                                                                        return (
                                                                            <td key={colIdx}>
                                                                                <span className="text-muted">
                                                                                    <i className="bx bx-x-circle font-size-20"></i>
                                                                                </span>
                                                                            </td>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            );
                                                        })}
                                                        {/* Add Total row */}
                                                        <tr>
                                                            <td className="fw-bold">Total</td>
                                                            {allPackages.map((pkg, idx) => (
                                                                <td key={pkg.id || idx} className="fw-bold">
                                                                    ${getTotalPriceForPackage(idx, allPackages).toFixed(2)}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                        {/* Add Select button row */}
                                                        <tr>
                                                            <td></td>
                                                            {allPackages.map((pkg, idx) => (
                                                                <td key={pkg.id || idx}>
                                                                    <button
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={() => handleSelectAndScroll(idx, allPackages)}
                                                                    >
                                                                        Select
                                                                    </button>
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })()}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <div id="cf-form-anchor"></div>
                    {formOpenPkgIdx !== null && (() => {
                        // Rebuild allPackages here to match the table logic
                        const allPackages: any[] = [];
                        plans.forEach(plan => {
                            (plan.productPackages || []).forEach(pkg => {
                                allPackages.push(pkg);
                            });
                        });
                        allPackages.sort((a, b) => (a.collectionDisplaySequence ?? 0) - (b.collectionDisplaySequence ?? 0));
                        (window as any).CFPlansPackageData = allPackages;
                        const totalPrice = getTotalPriceForPackage(formOpenPkgIdx, allPackages).toFixed(2);
                        return (
                            <div className="mt-4">
                                <CFForm
                                    productIds={getSelectedProductIds(formOpenPkgIdx)}
                                    businessStructureType={entityType}
                                    businessStateInitial={stateCode}
                                    packageId={allPackages[formOpenPkgIdx]?.id || ""}
                                    orderTotalPrice={totalPrice}
                                    onClose={() => {setFormOpenPkgIdx(null); fetch_plans(); fetch_orders();}}
                                />
                            </div>
                        );
                                    })()}</>)}
                            </TabPane>

                            {/* Contact Tab */}
                            <TabPane tabId="2">
                                <div style={{ padding: '24px 16px' }}>
                                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                                        <h4 style={{ marginBottom: '24px', color: '#333', fontWeight: 600 }}>Contact Us</h4>
                                        <p style={{ marginBottom: '32px', color: '#666', fontSize: '15px', lineHeight: 1.6 }}>
                                            Have questions about our company formation services? We're here to help. Reach out to us using the contact information below.
                                        </p>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                                            {/* Email Contact Card */}
                                            <div
                                                style={{
                                                    background: '#fff',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 12,
                                                    padding: '24px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                                                    e.currentTarget.style.borderColor = '#1976d2';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                                onClick={() => window.location.href = `mailto:${companyFormationContactEmail}`}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                                    <div style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #d93025 0%, #ea4335 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(217, 48, 37, 0.3)'
                                                    }}>
                                                        <i className="bx bx-envelope" style={{ fontSize: 24, color: '#fff' }}></i>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 16, color: '#333', marginBottom: '4px' }}>Email</div>
                                                        <div style={{ fontSize: 13, color: '#666' }}>Send us an email</div>
                                                    </div>
                                                </div>
                                                <a
                                                    href={`mailto:${companyFormationContactEmail}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        color: '#1976d2',
                                                        textDecoration: 'none',
                                                        fontSize: 15,
                                                        wordBreak: 'break-word',
                                                        display: 'block',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {companyFormationContactEmail}
                                                </a>
                                            </div>

                                            {/* Phone Contact Card */}
                                            <div
                                                style={{
                                                    background: '#fff',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 12,
                                                    padding: '24px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                                                    e.currentTarget.style.borderColor = '#34a853';
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                                onClick={() => window.location.href = `tel:${companyFormationContactPhoneRaw.replace(/\D/g, '')}`}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                                                    <div style={{
                                                        width: '48px',
                                                        height: '48px',
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #34a853 0%, #4caf50 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(52, 168, 83, 0.3)'
                                                    }}>
                                                        <i className="bx bx-phone" style={{ fontSize: 24, color: '#fff' }}></i>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 16, color: '#333', marginBottom: '4px' }}>Phone</div>
                                                        <div style={{ fontSize: 13, color: '#666' }}>Call us directly</div>
                                                    </div>
                                                </div>
                                                <a
                                                    href={`tel:${companyFormationContactPhoneRaw.replace(/\D/g, '')}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        color: '#1976d2',
                                                        textDecoration: 'none',
                                                        fontSize: 15,
                                                        display: 'block',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {companyFormationContactPhone}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabPane>
                        </TabContent>
                    </>
                )}

                {/* Upload Document Modal */}
                <Modal isOpen={uploadModal.open} toggle={() => setUploadModal({open: false, orderGuid: null})}>
                    <ModalHeader toggle={() => setUploadModal({open: false, orderGuid: null})}>
                        Upload Document
                    </ModalHeader>
                    <div className="modal-body">
                        <div className="mb-3">
                            <Label for="fileInput">Select Document</Label>
                            <input
                                type="file"
                                className="form-control"
                                id="fileInput"
                                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                            <small className="form-text text-muted">
                                Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG
                            </small>
                        </div>
                        {selectedFile && (
                            <div className="alert alert-info">
                                <strong>Selected file:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setUploadModal({open: false, orderGuid: null})}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={uploadDocument}
                            disabled={!selectedFile}
                        >
                            <i className="bx bx-upload me-1"></i>
                            Upload Document
                        </button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}